import React from 'react'
import { Filter, Layers, ChevronRight } from 'lucide-react'
import { getFieldValue, INSPECTOR_COLUMNS } from '../../config/columns'
import { getStrikeLabel, TABLE_CLASSES } from '../../lib/table-utils'
import type { AnalysisResult, OptionData } from '../../types'
import { type ColumnConfig, ColumnSelector } from '../ColumnSelector'
import { NumberCell } from '../ui/ghost-table/DataCells'
import { GhostTh } from '../ui/ghost-table/StickyHeader'
import { StrikeChip } from '../ui/ghost-table/StrikeChip'
import { TableWrapper } from '../ui/ghost-table/TableWrapper'

interface InspectorMatrixProps {
    columnConfigs: ColumnConfig[]
    handleColumnToggle: (id: string) => void
    rows: { ce?: OptionData; pe?: OptionData }[]
    visibleColumns: ColumnConfig[]
    totalItems: number
    analysis: AnalysisResult | null
    children?: React.ReactNode // For RSI Chart or other injected content
    mode: string
}

export const InspectorMatrix: React.FC<InspectorMatrixProps> = React.memo(
    ({
        columnConfigs,
        handleColumnToggle,
        rows,
        visibleColumns,
        totalItems,
        analysis,
        children,
        mode,
    }) => {
        const [rowsPerPage, setRowsPerPage] = React.useState(() => {
            if (typeof window !== 'undefined') {
                const s = localStorage.getItem('inspector_matrix_rows')
                return s ? parseInt(s, 10) : 10
            }
            return 10
        })

        const [visibleLimit, setVisibleLimit] = React.useState(rowsPerPage)

        React.useEffect(() => {
            if (typeof window !== 'undefined') {
                localStorage.setItem('inspector_matrix_rows', rowsPerPage.toString())
            }
            setVisibleLimit(rowsPerPage)
        }, [rowsPerPage])

        const paginatedRows = React.useMemo(() => {
            if (mode !== 'Single') return rows
            return rows.slice(0, visibleLimit)
        }, [rows, visibleLimit, mode])

        // Helper to find label for a strike
        const getLabelForStrike = (strike: number) => {
            return getStrikeLabel(strike, analysis)
        }

        const formatHeaderLabel = (label: string) => {
            if (label.includes(' (')) {
                const [main, sub] = label.split(' (')
                return (
                    <>
                        {main}
                        <br />
                        <span className="text-[10px] opacity-80">({sub}</span>
                    </>
                )
            }
            return label
        }

        // Optimization: Pre-calculate column definitions to avoid .find() in every cell render
        const dataColumns = React.useMemo(() => {
            return visibleColumns
                .filter((c) => c.id !== 'cum_price')
                .map((col) => {
                    const def = INSPECTOR_COLUMNS.find((d) => d.id === col.id)
                    return { ...col, def }
                })
        }, [visibleColumns])

        return (
            <TableWrapper className="p-0 border-none shadow-2xl group relative bg-white dark:bg-slate-900 ring-1 ring-white/60 dark:ring-white/10 border border-white/40 dark:border-white/10 transition-colors duration-300">
                <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none -rotate-12 group-hover:rotate-0 transition-transform duration-1000 overflow-hidden">
                    <Layers size={120} />
                </div>

                <div className="px-6 py-5 border-b border-indigo-100/20 dark:border-indigo-500/10 bg-white dark:bg-slate-900 flex flex-wrap gap-y-4 items-center justify-between relative z-10 transition-colors duration-300">
                    <div>
                        <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                            <Layers size={18} className="text-indigo-500 dark:text-indigo-400" />
                            Inspector Matrix
                        </h3>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold mt-1 uppercase tracking-widest">
                            Cross-Strike Comparative Data
                        </p>
                    </div>
                    <ColumnSelector columns={columnConfigs} onChange={handleColumnToggle} />
                </div>

                {children && <div className="mb-6">{children}</div>}

                <div className={`${TABLE_CLASSES.CONTAINER} max-h-[800px]`}>
                    <table className={TABLE_CLASSES.TABLE}>
                        <thead className={TABLE_CLASSES.THEAD}>
                            <tr>
                                {visibleColumns
                                    .filter((c) => c.id !== 'cum_price')
                                    .map((col) => (
                                        <GhostTh key={`ce-${col.id}`} align="right" color="red" className="top-0!">
                                            <div className="flex flex-col items-end">
                                                <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                    CE Call
                                                </span>
                                                {formatHeaderLabel(col.label)}
                                            </div>
                                        </GhostTh>
                                    ))}

                                <GhostTh align="center" color="red" className="top-0! min-w-[80px]">
                                    CE
                                    <br />
                                    Strike
                                </GhostTh>

                                {visibleColumns.find((c) => c.id === 'cum_price') && (
                                    <GhostTh align="center" color="purple" className="top-0!">
                                        <span className="text-[9px] opacity-60 uppercase font-black block">
                                            Combined
                                        </span>
                                        Total Prem
                                    </GhostTh>
                                )}

                                <GhostTh align="center" color="green" className="top-0! min-w-[80px]">
                                    PE
                                    <br />
                                    Strike
                                </GhostTh>

                                {visibleColumns
                                    .filter((c) => c.id !== 'cum_price')
                                    .map((col) => (
                                        <GhostTh key={`pe-${col.id}`} align="left" color="green" className="top-0!">
                                            <div className="flex flex-col items-start">
                                                <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                    PE Put
                                                </span>
                                                {formatHeaderLabel(col.label)}
                                            </div>
                                        </GhostTh>
                                    ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white/40 dark:bg-slate-900/40">
                            {paginatedRows.length > 0 ? (
                                paginatedRows.map((row, idx) => {
                                    // Generate a stable key based on content (Strikes) rather than index
                                    const rowKey = `matrix-${row.ce?.strike ?? 'x'}-${row.pe?.strike ?? 'x'}-${idx}`
                                    return (
                                        <tr key={rowKey} className={TABLE_CLASSES.TR}>
                                            {dataColumns.map(({ id, def }) => {
                                                let value =
                                                    row.ce && def?.ceField
                                                        ? getFieldValue(row.ce, def.ceField as readonly string[])
                                                        : undefined

                                                const isChange = id === 'change_p' || id === 'oi_change'

                                                // Apply scaling for specific formats
                                                if (value !== undefined && typeof value === 'number') {
                                                    if (def?.format === 'lakh') value = value / 100000
                                                    else if (def?.format === 'crore') value = value / 10000000
                                                }

                                                return (
                                                    <td
                                                        key={`ce-cell-${id}-${idx}`}
                                                        className={`${TABLE_CLASSES.TD} text-right text-xs`}
                                                    >
                                                        <NumberCell
                                                            value={value === undefined ? '-' : value}
                                                            isCurrency={def?.format === 'decimal'}
                                                            suffix={def?.format === 'percent' ? '%' : undefined}
                                                            className={
                                                                isChange && typeof value === 'number'
                                                                    ? value > 0
                                                                        ? 'text-emerald-600 dark:text-emerald-400'
                                                                        : value < 0
                                                                          ? 'text-rose-600 dark:text-rose-400'
                                                                          : 'text-slate-600 dark:text-slate-400'
                                                                    : 'text-slate-600 dark:text-slate-400'
                                                            }
                                                        />
                                                    </td>
                                                )
                                            })}

                                            <td className={`${TABLE_CLASSES.TD} text-center`}>
                                                {row.ce ? (
                                                    <StrikeChip
                                                        strike={row.ce.strike}
                                                        label={getLabelForStrike(row.ce.strike)?.text}
                                                        type={
                                                            getLabelForStrike(row.ce.strike)?.text
                                                                ? 'resistance'
                                                                : 'neutral'
                                                        }
                                                    />
                                                ) : (
                                                    <span className="opacity-20">-</span>
                                                )}
                                            </td>

                                            {visibleColumns.find((c) => c.id === 'cum_price') && (
                                                <td
                                                    className={`${TABLE_CLASSES.TD} bg-indigo-50/20 dark:bg-indigo-900/10 text-center font-black text-indigo-700 dark:text-indigo-400 shadow-inner`}
                                                >
                                                    <NumberCell
                                                        value={(row.ce?.callPrice || 0) + (row.pe?.putPrice || 0)}
                                                        className="text-indigo-700 dark:text-indigo-400"
                                                    />
                                                </td>
                                            )}

                                            <td className={`${TABLE_CLASSES.TD} text-center`}>
                                                {row.pe ? (
                                                    <StrikeChip
                                                        strike={row.pe.strike}
                                                        label={getLabelForStrike(row.pe.strike)?.text}
                                                        type={
                                                            getLabelForStrike(row.pe.strike)?.text
                                                                ? 'support'
                                                                : 'neutral'
                                                        }
                                                    />
                                                ) : (
                                                    <span className="opacity-20">-</span>
                                                )}
                                            </td>

                                            {dataColumns.map(({ id, def }) => {
                                                let value =
                                                    row.pe && def?.peField
                                                        ? getFieldValue(row.pe, def.peField as readonly string[])
                                                        : undefined
                                                const isChange = id === 'change_p' || id === 'oi_change'

                                                // Apply scaling
                                                if (value !== undefined && typeof value === 'number') {
                                                    if (def?.format === 'lakh') value = value / 100000
                                                    else if (def?.format === 'crore') value = value / 10000000
                                                }

                                                return (
                                                    <td
                                                        key={`pe-cell-${id}-${idx}`}
                                                        className={`${TABLE_CLASSES.TD} text-left text-xs`}
                                                    >
                                                        <NumberCell
                                                            value={value === undefined ? '-' : value}
                                                            isCurrency={def?.format === 'decimal'}
                                                            suffix={def?.format === 'percent' ? '%' : undefined}
                                                            className={
                                                                isChange && typeof value === 'number'
                                                                    ? value > 0
                                                                        ? 'text-emerald-600 dark:text-emerald-400'
                                                                        : value < 0
                                                                          ? 'text-rose-600 dark:text-rose-400'
                                                                          : 'text-slate-600 dark:text-slate-400'
                                                                    : 'text-slate-600 dark:text-slate-400'
                                                            }
                                                        />
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                })
                            ) : (
                                <tr>
                                    <td colSpan={25} className="py-24 text-center text-slate-300 dark:text-slate-600">
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-full">
                                                <Filter size={32} className="opacity-30" />
                                            </div>
                                            <div>
                                                <span className="text-sm font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                                                    {totalItems > 0 ? 'Data Hidden by Filters' : 'No Data Available'}
                                                </span>
                                                <p className="text-[10px] mt-1 font-medium italic">
                                                    {totalItems > 0
                                                        ? 'Try adjusting strike range or clearing filters.'
                                                        : 'No market snapshots found for this selection.'}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* ── Footer: Load More ── */}
                { mode === 'Single' && rows.length > visibleLimit && (
                    <div className="flex items-center justify-center p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20">
                        <button
                            onClick={ () => setVisibleLimit( prev => prev + rowsPerPage ) }
                            className="h-10 px-6 text-xs font-bold rounded-xl border border-primary/20 bg-primary/10 text-primary hover:bg-primary/20 transition-colors inline-flex items-center gap-2 shadow-sm"
                        >
                            Load Next { rowsPerPage } Rows
                            <span className="text-[10px] font-medium text-primary/70">
                                ({ rows.length - visibleLimit } remaining)
                            </span>
                            <ChevronRight className="h-4 w-4 opacity-70" />
                        </button>
                    </div>
                ) }
            </TableWrapper>
        )
    },
)
